import { addedId, trackName, withArticle } from './corrections';

/**
 * The words for one of people's edits, in one place: the cards of "Your changes" (ChangesList.jsx) and the rows of the
 * PDF report's Corrections page say the same. `subject` is one of the things counted ("reindeer"); `at` turns a frame
 * of the analysed clip into a time ("1:02"); `follow` is the job following added objects, while one runs.
 */

const TOOL = {
	click: 'click',
	box: 'box',
	assistant_hint: 'the assistant',
	manual: 'box',
	dot: 'you',
};

// a removal the analysis made itself, from what people removed in earlier videos of the same subject
const FILTER = 'filter';

// what the edit did, without the tracks it names: "Added · reindeer", "Not a reindeer", "Merged", "Removed",
// "Taken out"
const changeOf = (e, subject) => {
	if (e.type === 'add') return `Added · ${e.label || subject}`;
	if (e.type === 'merge') return 'Merged';
	if (e.type === 'negative') return `Not ${withArticle(e.label || subject)}`;
	return e.tool === FILTER ? 'Taken out' : 'Removed';
};

/** "Removed · #12 at 0:41", "Not a reindeer · #33", "Merged · #4 + #9", "Added · reindeer at 1:02". */
export const editTitle = (e, { subject, at }) => {
	const change = changeOf(e, subject);
	if (e.type === 'add') return `${change} at ${at(e.frame)}`;
	if (e.type === 'merge') return `${change} · ${(e.tracks || []).map(trackName).join(' + ')}`;
	const when = e.type !== 'negative' && e.frame != null ? ` at ${at(e.frame)}` : '';
	return `${change} · ${trackName(e.tracks?.[0])}${when}`;
};

/** An edit as a card: { kind: add | remove | merge | amber, title, sub, pending?, failed? }. */
export const editCard = (e, { subject, at, follow = null }) => {
	const tool = TOOL[e.tool] || 'you';
	const title = editTitle(e, { subject, at });
	if (e.status === 'unmatched') return { kind: 'amber', title, sub: 'not found in this version' };
	if (e.type === 'add') {
		if (e.status === 'following')
			return {
				kind: 'add',
				pending: true,
				title,
				sub:
					follow?.status === 'starting'
						? `${tool} · a machine is starting to follow it (about 2 min)`
						: `${tool} · being followed through the video…`,
			};
		if (e.status === 'failed')
			return {
				kind: 'amber',
				title,
				sub: 'could not follow — counts on this frame only',
				failed: true,
			};
		const t = e.track?.t;
		return {
			kind: 'add',
			title,
			sub: t?.length ? `${tool} · followed ${at(t[0])}–${at(t[t.length - 1])}` : tool,
		};
	}
	if (e.type === 'merge') return { kind: 'merge', title, sub: 'the same object' };
	return {
		kind: 'remove',
		title,
		sub:
			e.type === 'negative'
				? 'and everything like it, when run again'
				: e.tool === FILTER
					? 'like what was removed in earlier videos'
					: e.reason
						? `the assistant: ${e.reason}`
						: 'removed from every frame',
	};
};

/**
 * An edit as a row of the report's table: { kind, change, track, how }; the time has its own column. `place` is the
 * edit's 1-based place in the layer, which names an added object without a `seq` ("added #3").
 */
export const editRow = (e, { subject, at, follow = null, place }) => {
	const { kind, sub } = editCard(e, { subject, at, follow });
	const names = (e.tracks || []).map(trackName);
	const track =
		e.type === 'add'
			? trackName(addedId(e, place))
			: names.join(e.type === 'merge' ? ' + ' : ', ');
	return { kind, change: changeOf(e, subject), track, how: sub };
};
